import React, { useState, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
// IMPORTANTE: Importamos como modulo
import styles from './IntakeWizard.module.css'; 

const INITIAL_STATE = {
  projectName: '',
  sector: '',
  deliveryMethod: '',
  zipcode: '',
  budget: '',
  grossArea: '',
  areaUnit: 'sq ft',
  complexities: { f2: 3, f3: 3, f4: 3, f5: 3, f6: 3, f7: 3 },
  evidence: ''
};

const SECTORS = ['Vertical Building', 'Heavy Civil', 'Industrial', 'Infrastructure'];
const DELIVERY_METHODS = ['Design-Build', 'Design-Bid-Build', 'CMAR', 'PPP'];

const getComplexityLabel = (factor, value) => {
  if (factor === 'f5') {
    const labels = {
      1: "Clase 1 - Diseño Completo",
      2: "Clase 2 - Diseño Avanzado",
      3: "Clase 3 - Desarrollo de Diseño",
      4: "Clase 4 - Estudio de Viabilidad",
      5: "Clase 5 - Concepto Inicial"
    };
    return labels[value];
  }
  const generic = { 1: "Muy Bajo", 2: "Bajo", 3: "Moderado", 4: "Alto", 5: "Extremo" };
  return generic[value];
};

const IntakeWizard = ({ onCancel, onSave }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_STATE);

  useEffect(() => {
    const savedDraft = localStorage.getItem('projectIntakeDraft');
    if (savedDraft) {
      setFormData(JSON.parse(savedDraft));
    }
  }, []);

  const handleDataChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    localStorage.setItem('projectIntakeDraft', JSON.stringify(newData));
  };

  const handleComplexityChange = (factor, value) => {
    const newData = { ...formData, complexities: { ...formData.complexities, [factor]: value } };
    setFormData(newData);
    localStorage.setItem('projectIntakeDraft', JSON.stringify(newData));
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 4));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = () => {
    localStorage.removeItem('projectIntakeDraft');
    if (onSave) onSave(formData);
  };

  return (
    <div className={styles.wizardContainer}>
      <div className="container">
        <div className="row">
          
          {/* Columna Izquierda */}
          <div className="col-12 col-lg-7 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <h2 className={styles.sectionTitle}>Nuevo Proyecto</h2>
              {/* Botón sutil para cerrar el wizard y volver a la lista */}
              <button className="btn btn-link text-secondary" onClick={onCancel}>
                <i className="bi bi-x-circle fs-4"></i> Cerrar
              </button>
            </div>
            <p className={`${styles.stepSubtitle} mb-4`}>Paso {currentStep} de 4</p>

            <div className={styles.fadeInStep} key={currentStep}>
              {currentStep === 1 && (
                <div>
                  <h4 className="mb-4">Identidad y Marco</h4>
                  
                  <div className="form-group mb-4">
                    <label className="font-weight-bold text-secondary">Nombre del Proyecto</label>
                    <input 
                      type="text" 
                      className="form-control form-control-lg bg-white"
                      placeholder="Ej: Ampliación Terminal Sur"
                      value={formData.projectName}
                      onChange={(e) => handleDataChange('projectName', e.target.value)}
                    />
                  </div>

                  <div className="form-group mb-4">
                    <label className="font-weight-bold text-secondary">Sector de la Industria</label>
                    <div className="row">
                      {SECTORS.map(sec => (
                        <div className="col-6 col-md-3 mb-2" key={sec}>
                          <div 
                            className={`${styles.selectableCard} ${formData.sector === sec ? styles.selectableCardActive : ''}`}
                            onClick={() => handleDataChange('sector', sec)}
                          >
                            <small>{sec}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-group mb-4">
                    <label className="font-weight-bold text-secondary">Método de Entrega (Delivery Method)</label>
                    <div className="row">
                      {DELIVERY_METHODS.map(dm => (
                        <div className="col-6 mb-2" key={dm}>
                          <div 
                            className={`${styles.selectableCard} ${formData.deliveryMethod === dm ? styles.selectableCardActive : ''}`}
                            onClick={() => handleDataChange('deliveryMethod', dm)}
                          >
                            <span>{dm}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div>
                  <h4 className="mb-4">Escala y Entorno</h4>
                  <div className="form-group mb-4">
                    <label className="font-weight-bold text-secondary">Código Postal</label>
                    <input 
                      type="text" 
                      className="form-control bg-white"
                      value={formData.zipcode}
                      onChange={(e) => handleDataChange('zipcode', e.target.value)}
                    />
                  </div>
                  <div className="form-group mb-4">
                    <label className="font-weight-bold text-secondary">Presupuesto Estimado (TPC)</label>
                    <NumericFormat
                      className="form-control bg-white"
                      value={formData.budget}
                      thousandSeparator={true}
                      prefix={'$ '}
                      onValueChange={(values) => handleDataChange('budget', values.value)}
                    />
                  </div>
                  <div className="form-group mb-4">
                    <label className="font-weight-bold text-secondary">Área Bruta (Gross Area)</label>
                    <div className="input-group">
                      <input 
                        type="number" 
                        className="form-control bg-white" 
                        value={formData.grossArea}
                        onChange={(e) => handleDataChange('grossArea', e.target.value)}
                      />
                      <div className="input-group-append">
                        <select 
                          className="input-group-text bg-light"
                          value={formData.areaUnit}
                          onChange={(e) => handleDataChange('areaUnit', e.target.value)}
                        >
                          <option value="sq ft">sq ft</option>
                          <option value="m2">m2</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div>
                  <h4 className="mb-4">Matriz de Complejidad</h4>
                  {['f2', 'f3', 'f4', 'f5', 'f6', 'f7'].map((factor) => {
                    const factorNames = {
                      f2: 'F2 - Complejidad Organizacional', f3: 'F3 - Complejidad Técnica',
                      f4: 'F4 - Restricciones del Sitio', f5: 'F5 - Madurez de Estimación',
                      f6: 'F6 - Presión de Cronograma', f7: 'F7 - Importancia Estratégica'
                    };
                    return (
                      <div className="form-group mb-4" key={factor}>
                        <label className="d-block font-weight-bold text-secondary">{factorNames[factor]}</label>
                        <div className={`${styles.segmentedControl} mt-2 mb-1`}>
                          {[1, 2, 3, 4, 5].map(num => (
                            <button
                              key={num}
                              type="button"
                              className={formData.complexities[factor] === num ? styles.activeSegment : ''}
                              onClick={() => handleComplexityChange(factor, num)}
                            >
                              {num}
                            </button>
                          ))}
                        </div>
                        <small className={`d-block ${styles.accentText} font-weight-bold`}>
                          {getComplexityLabel(factor, formData.complexities[factor])}
                        </small>
                      </div>
                    );
                  })}
                </div>
              )}

              {currentStep === 4 && (
                <div>
                  <h4 className="mb-4">Auditoría y Cierre</h4>
                  <div className="form-group mb-4">
                    <label className="font-weight-bold text-secondary">Justificación de Complejidad</label>
                    <textarea 
                      className="form-control bg-white" 
                      rows="6"
                      value={formData.evidence}
                      onChange={(e) => handleDataChange('evidence', e.target.value)}
                    ></textarea>
                  </div>
                </div>
              )}
            </div>

            {/* Controles */}
            <div className="d-flex justify-content-between mt-5 pt-3 border-top">
              <button 
                className={`btn ${styles.btnOutline} px-4`} 
                onClick={prevStep} 
                disabled={currentStep === 1}
              >
                Volver
              </button>
              {currentStep < 4 ? (
                <button className={`btn ${styles.btnPrimary} px-4`} onClick={nextStep}>
                  Continuar
                </button>
              ) : (
                <button className={`btn ${styles.btnPrimary} px-4`} onClick={handleSubmit}>
                  Crear Proyecto
                </button>
              )}
            </div>
          </div>

          {/* Columna Derecha (Preview) */}
          <div className="col-12 col-lg-5 d-none d-lg-block">
            <div className={styles.livePreviewCard}>
              <h5 className={`mb-4 text-uppercase ${styles.accentText}`} style={{ letterSpacing: '1px', fontSize: '0.9rem', fontWeight: 'bold' }}>
                Resumen del Proyecto
              </h5>
              
              <div className="mb-3">
                <small className="text-muted d-block">Nombre</small>
                <span style={{ fontSize: '1.2rem', fontWeight: '600', color: '#212529' }}>
                  {formData.projectName || 'Sin título'}
                </span>
              </div>

              <div className="row mb-3">
                <div className="col-6">
                  <small className="text-muted d-block">Sector</small>
                  <span className="text-dark font-weight-bold">{formData.sector || '-'}</span>
                </div>
                <div className="col-6">
                  <small className="text-muted d-block">Entrega</small>
                  <span className="text-dark font-weight-bold">{formData.deliveryMethod || '-'}</span>
                </div>
              </div>

              <hr />

              <div className="row mb-3">
                <div className="col-6">
                  <small className="text-muted d-block">Presupuesto</small>
                  <span className="text-dark font-weight-bold">
                    {formData.budget ? `$ ${formData.budget}` : '-'}
                  </span>
                </div>
                <div className="col-6">
                  <small className="text-muted d-block">Área</small>
                  <span className="text-dark font-weight-bold">{formData.grossArea ? `${formData.grossArea} ${formData.areaUnit}` : '-'}</span>
                </div>
              </div>

              <hr />

              <div>
                <small className="text-muted d-block mb-2">Complejidad (1-5)</small>
                <div className="d-flex justify-content-between text-center">
                  {Object.entries(formData.complexities).map(([key, value]) => (
                    <div key={key}>
                      <small className="d-block text-muted" style={{ fontSize: '0.75rem' }}>{key.toUpperCase()}</small>
                      <span className={`badge ${value > 3 ? 'badge-danger' : value === 3 ? 'badge-warning text-dark' : 'badge-success'}`}>
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default IntakeWizard;